import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Alert, Button, Typography } from "@mui/material";
import {
  type Context,
  DataRoomContextProvider,
  DataRoomSubmission,
  updateSurveySubmission,
} from "@coolset/data-room";
import { routes } from "config";
import { previewClient } from "modules/Preview/client/previewClient";
import { usePreviewSession } from "modules/Preview/hooks/usePreviewSession";
import { CoolsetPageLoader } from "shared/components/Loader/CoolsetLoader";
import "modules/Preview/styles/preview.css";

const noop = () => null;
const emptyList = async () => [];

export const SurveyPreview = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { session, isLoading, isError, reset } = usePreviewSession(surveyId!);

  const handleQuestionAnswered = useCallback(
    async (fieldId: string, context: Context, value: string) => {
      if (!session) return;
      const delta = await previewClient.postPreviewAnswer({
        submissionId: session.surveySubmissionId,
        fieldId,
        context,
        value,
      });
      // The delta replaces the websocket path the consumer app uses;
      // updateSurveySubmission needs the submission id to route it.
      updateSurveySubmission({ id: session.surveySubmissionId, ...delta });
    },
    [session],
  );

  if (isLoading) {
    return <CoolsetPageLoader />;
  }

  if (isError || !session) {
    return (
      <div className="preview-error">
        <Alert severity="error">
          Could not create a preview for this survey. Make sure the survey has
          at least one block, then try again.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(routes.survey(surveyId!))}
        >
          Back to editor
        </Button>
      </div>
    );
  }

  return (
    <div className="preview-page">
      <div className="preview-banner">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(routes.survey(surveyId!))}
        >
          Back to editor
        </Button>
        <Typography color="text.secondary">
          Previewing — answers are not saved
        </Typography>
        <Button startIcon={<RestartAltIcon />} onClick={() => reset()}>
          Reset preview
        </Button>
      </div>
      <div className="preview-survey">
        <DataRoomContextProvider
          getSurveySubmissionCallback={previewClient.getPreviewSubmission}
          onQuestionAnswered={handleQuestionAnswered}
          getCommentsCallback={emptyList}
          getContributorsCallback={emptyList}
          getDocumentsCallback={emptyList}
          listDocumentsCallback={emptyList}
          onCommentCreate={noop}
          onCommentDelete={noop}
          onCommentEdit={noop}
          onContributorCreate={noop}
          onContributorDelete={noop}
          onDocumentAttach={noop}
          onDocumentCreate={async () => undefined}
          onDocumentDelete={noop}
          onDocumentDetach={noop}
          onDocumentDownload={noop}
          onStatusChange={noop}
          onAutofill={noop}
        >
          <DataRoomSubmission
            key={session.surveySubmissionId}
            activeContext={session.queryContext}
            defaultSurveyId={session.surveyId}
            filterOptions={[]}
            surveySubmissionId={session.surveySubmissionId}
            rootSurveyOptions={{ isShowProgress: true, isHideHeader: false }}
            users={[]}
          />
        </DataRoomContextProvider>
      </div>
    </div>
  );
};
